import React from "react";
import { Link } from "react-router-dom";

/**
 * Privacy — GDPR + CCPA-friendly Privacy Policy. Written in plain
 * English rather than boilerplate so users can actually understand
 * what we collect and why.
 */
export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#050810] text-white pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-tv-accent text-[10px] uppercase tracking-[0.3em] font-black mb-2">Legal</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-2">Privacy Policy</h1>
        <div className="text-white/40 text-xs mb-8">Effective February 2026 · Tap In TV Network</div>

        <div className="prose prose-invert prose-sm max-w-none text-white/80 space-y-5">
          <section>
            <h2 className="font-display font-black text-white text-lg mt-2">Who this covers</h2>
            <p>This policy explains how Tap In TV Network collects, uses, and shares data about visitors, viewers, and creators.</p>
          </section>
          <section>
            <h2 className="font-display font-black text-white text-lg">What we collect</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account:</strong> email, display name, handle, birth year (age gate), password hash.</li>
              <li><strong>Profile:</strong> avatar, bio, social links you choose to publish (YouTube, IG, TikTok, X, Facebook, Website).</li>
              <li><strong>Usage:</strong> videos watched, streams joined, purchases, chat messages, tips, gifts.</li>
              <li><strong>Device:</strong> browser, IP address (kept 90 days for abuse prevention), timezone, screen size.</li>
              <li><strong>Payments:</strong> handled by Stripe — we store transaction records, never card numbers.</li>
            </ul>
          </section>
          <section>
            <h2 className="font-display font-black text-white text-lg">How we use it</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Operate the platform: serve streams, save videos, process payments, deliver notifications.</li>
              <li>Personalise recommendations (categories, live-now feed).</li>
              <li>Prevent fraud, abuse, and copyright infringement.</li>
              <li>Send transactional emails (password reset, purchase receipts, live alerts you subscribed to).</li>
            </ul>
          </section>
          <section>
            <h2 className="font-display font-black text-white text-lg">Who we share it with</h2>
            <p>Only with sub-processors required to run the service: Stripe (payments), Cloudflare Stream (video), Resend (email), Twilio (SMS you opt into), Google/Apple (auth), Emergent (LLM helpers). We do <strong>not</strong> sell your data.</p>
          </section>
          <section>
            <h2 className="font-display font-black text-white text-lg">Your rights (GDPR / CCPA)</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access &amp; export:</strong> email <a className="text-tv-accent hover:underline" href="mailto:privacy@tapintvnetwork.com">privacy@tapintvnetwork.com</a> and we&apos;ll ship a ZIP of your data within 30 days.</li>
              <li><strong>Delete:</strong> use the in-app delete-account button (Studio / Settings) or the email above.</li>
              <li><strong>Correct:</strong> update your profile in Studio at any time.</li>
              <li><strong>Opt-out of tracking:</strong> use our cookie banner to reject non-essential cookies.</li>
            </ul>
          </section>
          <section>
            <h2 className="font-display font-black text-white text-lg">Cookies</h2>
            <p>Essential cookies (session, CSRF) are always on. Optional analytics/perf cookies fire only after you accept them in the cookie banner. You can change your mind anytime via Settings.</p>
          </section>
          <section>
            <h2 className="font-display font-black text-white text-lg">Kids &amp; minors</h2>
            <p>Under-13 accounts are not permitted. Under-18 accounts cannot access adult content or make purchases without a parent-linked wallet. Report suspicious accounts via the &ldquo;Report&rdquo; button.</p>
          </section>
          <section>
            <h2 className="font-display font-black text-white text-lg">Contact</h2>
            <p>Data protection: <a className="text-tv-accent hover:underline" href="mailto:privacy@tapintvnetwork.com">privacy@tapintvnetwork.com</a>. See also our <Link to="/terms" className="text-tv-accent hover:underline">Terms of Service</Link>.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
